import type { EmailLog } from '../../../lib/emailsApi';

interface EmailsTableProps {
  emails: EmailLog[];
  sort: string;
  order: 'asc' | 'desc';
  onSort: (field: string) => void;
  onRowClick: (email: EmailLog) => void;
}

const STATUS_STYLES: Record<string, string> = {
  SENT: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function SortHeader({
  label,
  field,
  sort,
  order,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  sort: string;
  order: 'asc' | 'desc';
  onSort: (field: string) => void;
  align?: 'left' | 'right';
}) {
  const active = sort === field;
  return (
    <th className={`px-3 py-2 font-medium text-gray-600 text-${align}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        {active && <span aria-hidden>{order === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

export function EmailsTable({ emails, sort, order, onSort, onRowClick }: EmailsTableProps) {
  if (emails.length === 0) {
    return <p className="py-8 text-center text-gray-500">No emails found.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Recipient</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Subject</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Template</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
            <SortHeader label="Sent at" field="sentAt" sort={sort} order={order} onSort={onSort} />
            <SortHeader
              label="Opens"
              field="openCount"
              sort={sort}
              order={order}
              onSort={onSort}
              align="right"
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">First opened</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Last opened</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {emails.map((email) => (
            <tr
              key={email.id}
              onClick={() => onRowClick(email)}
              className="cursor-pointer hover:bg-gray-50"
            >
              <td className="px-3 py-2 font-medium text-gray-900">{email.to}</td>
              <td className="max-w-xs truncate px-3 py-2 text-gray-700" title={email.subject}>
                {email.subject}
              </td>
              <td className="px-3 py-2 text-gray-700">{email.template}</td>
              <td className="px-3 py-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    STATUS_STYLES[email.status] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {email.status}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-700">{formatDateTime(email.sentAt)}</td>
              <td className="px-3 py-2 text-right text-gray-900">{email.openCount}</td>
              <td className="px-3 py-2 text-gray-700">{formatDateTime(email.firstOpenedAt)}</td>
              <td className="px-3 py-2 text-gray-700">{formatDateTime(email.lastOpenedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
